import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Per-module admin settings — mirrors server/moduleSettings.ts. */

export type SettingModule = 'system' | 'psa' | 'rmm' | 'cipp' | 'csp' | 'backup' | 'insights' | 'network' | 'domains' | 'soc';

interface Base {
  key: string;
  module: SettingModule;
  group: string;
  label: string;
  description: string;
  public?: boolean;
  /** Roadmap placeholder — shown disabled. */
  planned?: boolean;
  isDefault: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type AdminSetting =
  | (Base & { type: 'number'; default: number; value: number; min: number; max: number; unit?: string })
  | (Base & { type: 'boolean'; default: boolean; value: boolean })
  | (Base & { type: 'emails'; default: string[]; value: string[] })
  | (Base & { type: 'text'; default: string; value: string; maxLength: number })
  | (Base & { type: 'select'; default: string; value: string; options: { value: string; label: string }[] })
  | (Base & { type: 'list'; default: string[]; value: string[]; placeholder?: string; patternHint?: string })
  /** Read-only: lives in the environment (App Service settings). */
  | (Base & { type: 'env'; envVar: string; default: string; value: string });

const ADMIN_KEY = ['admin', 'settings'] as const;
const PUBLIC_KEY = ['settings', 'public'] as const;

export function useAdminSettings() {
  return useQuery({ queryKey: ADMIN_KEY, queryFn: () => api.get<{ items: AdminSetting[] }>('/api/admin/settings').then((r) => r.items), staleTime: 30_000 });
}

export function useSaveAdminSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown | undefined }) =>
      (value === undefined
        ? api.del<{ items: AdminSetting[] }>(`/api/admin/settings/${encodeURIComponent(key)}`)
        : api.put<{ items: AdminSetting[] }>(`/api/admin/settings/${encodeURIComponent(key)}`, { value })).then((r) => r.items),
    onSuccess: (items) => {
      qc.setQueryData(ADMIN_KEY, items);
      qc.invalidateQueries({ queryKey: PUBLIC_KEY });
    },
  });
}

/** Settings any signed-in user can read (`public` in the registry). */
export function usePublicSettings() {
  return useQuery({ queryKey: PUBLIC_KEY, queryFn: () => api.get<Record<string, unknown>>('/api/settings'), staleTime: 10 * 60_000 });
}

/** One public setting with a fallback while loading. */
export function usePublicSetting<T>(key: string, fallback: T): T {
  const { data } = usePublicSettings();
  return (data?.[key] as T | undefined) ?? fallback;
}
